/**
 * BlogCard - Post preview card for blog listings
 */

import { Link } from "react-router-dom";
import type { BlogPost } from "../../lib/blog";
import { getCategoryBySlug } from "../../lib/blog";

interface BlogCardProps {
  post: BlogPost;
}

const CATEGORY_GRADIENTS: Record<string, string> = {
  "early-coding": "from-pixel-light/50 to-pixel/20",
  "screen-time": "from-magic-100 to-magic-50",
  "kindergarten-prep": "from-sunshine-100 to-sunshine-50",
  "learning-through-play": "from-meadow-100 to-meadow-50",
};

export function BlogCard({ post }: BlogCardProps) {
  const { frontmatter, category, slug } = post;
  const categoryInfo = getCategoryBySlug(category);
  const gradient = CATEGORY_GRADIENTS[category] || "from-neutral-100 to-neutral-50";

  // Format date
  const formattedDate = new Date(frontmatter.date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <Link
      to={`/blog/${category}/${slug}`}
      className="group block"
    >
      <article className="rounded-2xl overflow-hidden border-2 border-neutral-200 shadow-playful hover:shadow-playful-lg hover:-translate-y-1 transition-all duration-300 bg-white">
        {/* Gradient header */}
        <div className={`h-32 bg-gradient-to-br ${gradient}`} />

        {/* Content */}
        <div className="p-6">
          {/* Category tag */}
          <span className="inline-block px-3 py-1.5 bg-neutral-100 rounded-full font-body text-body-xs font-semibold text-neutral-600 mb-3" style={{ lineHeight: '1.8' }}>
            {categoryInfo?.name || category}
          </span>

          {/* Title */}
          <h3 className="font-display font-bold text-body-lg text-neutral-900 mb-2 line-clamp-2 group-hover:text-magic-600 transition-colors">
            {frontmatter.title}
          </h3>

          {/* Description */}
          <p className="font-body text-body-sm text-neutral-600 mb-4 line-clamp-2">
            {frontmatter.description}
          </p>

          {/* Meta */}
          <div className="flex items-center justify-between font-body text-body-xs text-neutral-500">
            <span>{formattedDate}</span>
            <span className="group-hover:text-magic-600 transition-colors">
              Read more →
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
